/* eslint-disable react/prop-types */
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
  Area,
  Line,
} from "recharts";
import { ChevronDown } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { dashboardLinks } from "@/components/lib/SidebarLinks";
import DashboardRangeTabs from "@/components/dashboard/dashboard-range-tabs";
import { Skeleton } from "@/components/ui/skeleton";
import useAuth from "@/hooks/useAuth";
import { fetchCallLogsReports } from "@/redux/slice/dashboard/dashboard-slice";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const value = Number(payload[0]?.value || 0);
    return (
      <div className="bg-[#2A2D35] border border-white/10 px-5 py-2.5 rounded shadow-xl flex flex-col items-center relative">
        <p className="text-white text-sm font-medium">
          {value.toLocaleString()}
        </p>
        <p className="text-[#9CA3AF] text-[11px] mt-0.5">{label}</p>
        <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-[#2A2D35] border-b border-r border-white/10 rotate-45"></div>
      </div>
    );
  }
  return null;
};

const StatCard = ({ title, value, valueColor, loading }) => (
  <div className="bg-[#151515] border border-white/5 rounded-xl p-6 h-[140px] flex flex-col justify-between shadow-sm">
    <p className="text-gray-300 text-[15px]">{title}</p>
    {loading ? (
      <Skeleton className="h-10 w-20" />
    ) : (
      <p className="text-[32px] font-semibold" style={{ color: valueColor }}>
        {Number(value || 0).toLocaleString()}
      </p>
    )}
  </div>
);

const ChartSkeleton = ({ height }) => (
  <div className="w-full" style={{ height }}>
    <Skeleton className="h-full w-full" />
  </div>
);

function CallLogReports() {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const yearOptions = useMemo(
    () => [currentYear, currentYear - 1],
    [currentYear],
  );

  const recentMonths = useMemo(
    () =>
      [2, 1, 0].map((offset) => {
        const date = new Date(currentYear, currentMonth - 1 - offset, 1);
        return {
          value: date.getMonth() + 1,
          label: MONTH_NAMES[date.getMonth()],
        };
      }),
    [currentMonth, currentYear],
  );

  const [comparisonRange, setComparisonRange] = useState("weekly");
  const [comparisonYear, setComparisonYear] = useState(currentYear);
  const [comparisonMonth, setComparisonMonth] = useState(currentMonth);

  const [totalCallRange, setTotalCallRange] = useState("monthly");
  const [totalYear, setTotalYear] = useState(currentYear);
  const [totalMonth, setTotalMonth] = useState(currentMonth);

  const hasLoadedInitialRef = useRef(false);

  const reportData = useSelector((state) => state.dashboard?.callLogsReports);
  const callLogsLoading = useSelector(
    (state) => state.dashboard?.callLogsLoading,
  );
  const restaurantId = user?.restaurant_id;

  const comparisonData = useMemo(
    () => reportData?.charts?.comparison?.[comparisonRange] || [],
    [reportData, comparisonRange],
  );

  const totalCallData = useMemo(
    () => reportData?.charts?.totalCall?.[totalCallRange] || [],
    [reportData, totalCallRange],
  );

  const totalCallFromFilteredData = useMemo(
    () =>
      totalCallData.reduce((acc, item) => acc + Number(item?.calls || 0), 0),
    [totalCallData],
  );

  const isInitialLoading = Boolean(callLogsLoading?.initial);
  const isComparisonLoading =
    isInitialLoading || Boolean(callLogsLoading?.comparison);
  const isTotalLoading =
    isInitialLoading || Boolean(callLogsLoading?.totalCall);

  const requestComparisonData = (
    period,
    year = comparisonYear,
    month = comparisonMonth,
  ) => {
    const params =
      period === "monthly"
        ? { period: "monthly", year, month }
        : { period: "weekly" };

    dispatch(
      fetchCallLogsReports({
        restaurant_id: restaurantId,
        params,
        requestMeta: { chart: "comparison" },
      }),
    );
  };

  const requestTotalCallData = (
    period,
    year = totalYear,
    month = totalMonth,
  ) => {
    const params =
      period === "yearly"
        ? { period: "yearly", year }
        : { period: "monthly", year, month };

    dispatch(
      fetchCallLogsReports({
        restaurant_id: restaurantId,
        params,
        requestMeta: { chart: "totalCall" },
      }),
    );
  };

  useEffect(() => {
    if (!restaurantId || hasLoadedInitialRef.current) return;

    dispatch(
      fetchCallLogsReports({
        restaurant_id: restaurantId,
        params: { period: "weekly" },
        requestMeta: { mode: "initial" },
      }),
    );

    hasLoadedInitialRef.current = true;
  }, [dispatch, restaurantId]);

  const handleComparisonRangeChange = (value) => {
    setComparisonRange(value);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    if (value === "weekly") {
      requestComparisonData("weekly");
      return;
    }

    requestComparisonData("monthly", comparisonYear, comparisonMonth);
  };

  const handleComparisonYearChange = (year) => {
    setComparisonYear(year);

    if (
      !hasLoadedInitialRef.current ||
      !restaurantId ||
      comparisonRange !== "monthly"
    )
      return;

    requestComparisonData("monthly", year, comparisonMonth);
  };

  const handleComparisonMonthChange = (month) => {
    setComparisonMonth(month);

    if (
      !hasLoadedInitialRef.current ||
      !restaurantId ||
      comparisonRange !== "monthly"
    )
      return;

    requestComparisonData("monthly", comparisonYear, month);
  };

  const handleTotalRangeChange = (value) => {
    setTotalCallRange(value);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    if (value === "yearly") {
      requestTotalCallData("yearly", totalYear);
      return;
    }

    requestTotalCallData("monthly", totalYear, totalMonth);
  };

  const handleTotalYearChange = (year) => {
    setTotalYear(year);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    if (totalCallRange === "yearly") {
      requestTotalCallData("yearly", year);
      return;
    }

    requestTotalCallData("monthly", year, totalMonth);
  };

  const handleTotalMonthChange = (month) => {
    setTotalMonth(month);

    if (
      !hasLoadedInitialRef.current ||
      !restaurantId ||
      totalCallRange !== "monthly"
    )
      return;

    requestTotalCallData("monthly", totalYear, month);
  };

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={dashboardLinks} />}
      pageName={{ title: "Call Log Reports" }}
      paths={[
        { name: "Dashboard", path: "/dashboard/overview" },
        { name: "Reports & Analytics", path: "/dashboard/revenue-reports" },
        { name: "Call Log", path: "/dashboard/call-log-reports" },
      ]}
    >
      <div className="container mx-auto p-6 space-y-8 min-h-screen text-white font-sans">
        <div className="flex justify-between items-center pt-2">
          <h1 className="text-xl font-medium tracking-wide">Call Log</h1>
          <div className="flex items-center gap-3">
            {comparisonRange === "monthly" && (
              <>
                <div className="relative">
                  <select
                    className="bg-[#1A1A1A] rounded-md px-3 py-1.5 border border-white/10 text-sm appearance-none pr-8"
                    value={comparisonMonth}
                    onChange={(e) =>
                      handleComparisonMonthChange(Number(e.target.value))
                    }
                  >
                    {recentMonths.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
                <div className="relative">
                  <select
                    className="bg-[#1A1A1A] rounded-md px-4 py-1.5 border border-white/10 text-sm appearance-none pr-8"
                    value={comparisonYear}
                    onChange={(e) =>
                      handleComparisonYearChange(Number(e.target.value))
                    }
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              </>
            )}
            <DashboardRangeTabs
              options={[
                { label: "Weekly", value: "weekly" },
                { label: "Monthly", value: "monthly" },
              ]}
              value={comparisonRange}
              onChange={handleComparisonRangeChange}
              shape="full"
              className="border-white/10"
              buttonClassName="px-4 md:px-5 py-1.5 text-xs"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Takeaway"
            value={reportData?.stats?.takeaway || 0}
            valueColor="#10B981"
            loading={isInitialLoading && !reportData}
          />
          <StatCard
            title="Delivery"
            value={reportData?.stats?.delivery || 0}
            valueColor="#3B82F6"
            loading={isInitialLoading && !reportData}
          />
          <StatCard
            title="Reservation"
            value={reportData?.stats?.reservation || 0}
            valueColor="#A855F7"
            loading={isInitialLoading && !reportData}
          />
          <StatCard
            title="Total Call"
            value={reportData?.stats?.totalCall || 0}
            valueColor="#FBBF24"
            loading={isInitialLoading && !reportData}
          />
        </div>

        <div className="bg-[#151515] border border-white/5 rounded-xl p-7 relative shadow-sm">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-[16px] font-medium text-gray-100">
              {comparisonRange === "weekly" ? "Weekly" : "Monthly"} Comparison
            </h2>
            <div className="flex gap-5 text-[13px] text-gray-300">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#10B981]" /> Takeaway
              </span>
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#3B82F6]" /> Delivery
              </span>
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#A855F7]" />{" "}
                Reservation
              </span>
            </div>
          </div>

          <div className="h-[420px] w-full">
            {isComparisonLoading ? (
              <ChartSkeleton height={420} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={comparisonData}
                  margin={{ top: 20, right: 0, left: -20, bottom: 18 }}
                >
                  <defs>
                    <linearGradient
                      id="deliveryGlow"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    vertical={false}
                    stroke="#374151"
                    strokeOpacity={0.4}
                  />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9CA3AF", fontSize: 12 }}
                    tickMargin={10}
                    dy={15}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9CA3AF", fontSize: 12 }}
                    dx={-10}
                  />
                  <Tooltip
                    cursor={{
                      stroke: "#4B5563",
                      strokeWidth: 1,
                      strokeDasharray: "3 3",
                    }}
                    content={<CustomTooltip />}
                  />

                  <Area
                    type="monotone"
                    dataKey="delivery"
                    stroke="#3B82F6"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#deliveryGlow)"
                    dot={false}
                    activeDot={{
                      r: 6,
                      fill: "#3B82F6",
                      stroke: "#fff",
                      strokeWidth: 2,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="takeaway"
                    stroke="#10B981"
                    strokeWidth={2.5}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="reservation"
                    stroke="#A855F7"
                    strokeWidth={2.5}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-[#151515] border border-white/5 rounded-xl p-7 shadow-sm">
          <div className="flex justify-between items-start mb-8">
            <div className="space-y-1">
              <h2 className="text-[16px] font-medium text-gray-100 flex items-center gap-2">
                Total <span className="text-white">Call</span>
              </h2>
              {isTotalLoading ? (
                <Skeleton className="h-10 w-24" />
              ) : (
                <p className="text-[32px] font-semibold text-[#FBBF24]">
                  {totalCallFromFilteredData.toLocaleString()}
                </p>
              )}
            </div>
            <div className="flex items-center gap-4">
              {totalCallRange === "monthly" && (
                <div className="relative">
                  <select
                    className="bg-[#1A1A1A] rounded-md px-3 py-1.5 border border-white/10 text-sm appearance-none pr-8"
                    value={totalMonth}
                    onChange={(e) =>
                      handleTotalMonthChange(Number(e.target.value))
                    }
                  >
                    {recentMonths.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              )}

              <div className="relative">
                <select
                  className="bg-[#1A1A1A] rounded-md px-4 py-1.5 border border-white/10 text-sm appearance-none pr-8"
                  value={totalYear}
                  onChange={(e) =>
                    handleTotalYearChange(Number(e.target.value))
                  }
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                />
              </div>

              <DashboardRangeTabs
                options={[
                  { label: "Monthly", value: "monthly" },
                  { label: "Yearly", value: "yearly" },
                ]}
                value={totalCallRange}
                onChange={handleTotalRangeChange}
                shape="full"
                className="border-white/10"
                buttonClassName="px-5 py-1 text-sm"
              />
            </div>
          </div>

          <div className="h-[380px] w-full">
            {isTotalLoading ? (
              <ChartSkeleton height={380} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={totalCallData}
                  margin={{ top: 20, right: 0, left: -20, bottom: 18 }}
                >
                  <CartesianGrid
                    vertical={false}
                    stroke="#374151"
                    strokeDasharray="3 3"
                    opacity={0.6}
                  />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9CA3AF", fontSize: 13 }}
                    tickMargin={10}
                    dy={15}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9CA3AF", fontSize: 12 }}
                    dx={-10}
                  />
                  <Tooltip
                    cursor={{ fill: "#ffffff", opacity: 0.04 }}
                    content={<CustomTooltip />}
                  />
                  <Bar
                    dataKey="calls"
                    fill="#A855F7"
                    radius={[3, 3, 0, 0]}
                    barSize={16}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </Layout2>
  );
}

export default CallLogReports;
